import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api, { isPlatformHostname } from "./api";

const MerchantCtx = createContext(null);
const PlatformCtx = createContext(null);
const CustomerCtx = createContext(null);

export function MerchantAuthProvider({ children }) {
  const [state, setState] = useState({ loading: true, user: null, tenant: null });
  const loadGen = useRef(0);

  const load = useCallback(async () => {
    const gen = ++loadGen.current;
    try {
      const { data } = await api.get("/auth/merchant/me");
      if (gen !== loadGen.current) return;
      setState({ loading: false, user: data.user, tenant: data.tenant });
    } catch {
      if (gen !== loadGen.current) return;
      setState({ loading: false, user: null, tenant: null });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const login = async (email, password) => {
    // Step 1: validate password -> backend emails an OTP. No session yet.
    const { data } = await api.post("/auth/merchant/login", { email, password });
    return data; // { otp_required: true, email }
  };
  const verifyLoginOtp = async (email, code) => {
    const { data } = await api.post("/auth/merchant/login/verify-otp", { email, code });
    setState({ loading: false, user: data.user, tenant: data.tenant });
    return data;
  };
  const signup = async (payload) => {
    const { data } = await api.post("/auth/merchant/signup", payload);
    setState({ loading: false, user: data.user, tenant: data.tenant });
    return data;
  };
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setState({ loading: false, user: null, tenant: null });
  };

  return (
    <MerchantCtx.Provider value={{ ...state, login, verifyLoginOtp, signup, logout, refresh: load }}>
      {children}
    </MerchantCtx.Provider>
  );
}
export const useMerchantAuth = () => useContext(MerchantCtx);

export function PlatformAuthProvider({ children }) {
  const [state, setState] = useState({ loading: true, user: null });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/platform/me");
      setState({ loading: false, user: data.user });
    } catch {
      setState({ loading: false, user: null });
    }
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined" && !isPlatformHostname(window.location.hostname)) {
      setState({ loading: false, user: null });
      return undefined;
    }
    load();
  }, [load]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/platform/login", { email, password });
    setState({ loading: false, user: data.user });
    return data;
  };
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setState({ loading: false, user: null });
  };
  return (
    <PlatformCtx.Provider value={{ ...state, login, logout }}>
      {children}
    </PlatformCtx.Provider>
  );
}
export const usePlatformAuth = () => useContext(PlatformCtx);

const SalesCtx = createContext(null);

export function SalesAuthProvider({ children }) {
  const [state, setState] = useState({ loading: true, user: null });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/sales/me");
      setState({ loading: false, user: data.user });
    } catch {
      setState({ loading: false, user: null });
    }
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined" && !isPlatformHostname(window.location.hostname)) {
      setState({ loading: false, user: null });
      return undefined;
    }
    load();
  }, [load]);

  const login = async (username, password) => {
    const { data } = await api.post("/auth/sales/login", { username, password });
    setState({ loading: false, user: data.user });
    return data;
  };
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setState({ loading: false, user: null });
  };
  return (
    <SalesCtx.Provider value={{ ...state, login, logout }}>
      {children}
    </SalesCtx.Provider>
  );
}
export const useSalesAuth = () => useContext(SalesCtx);

export function CustomerAuthProvider({ slug, children }) {
  const [state, setState] = useState({ loading: true, user: null });
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/customer/me");
      // If the customer belongs to a different tenant, treat as unauth for this store
      // The backend already scopes by JWT tenant_id, but UI must respect slug boundary.
      setState({ loading: false, user: data.user });
    } catch {
      setState({ loading: false, user: null });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/customer/login", { store_slug: slug, email, password });
    setState({ loading: false, user: data.user });
    return data;
  };
  const loginPhone = async (firebase_id_token) => {
    const { data } = await api.post("/auth/customer/login-phone", { store_slug: slug, firebase_id_token });
    setState({ loading: false, user: data.user });
    return data;
  };
  const signup = async (payload) => {
    const { data } = await api.post("/auth/customer/signup", { ...payload, store_slug: slug });
    setState({ loading: false, user: data.user });
    return data;
  };
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setState({ loading: false, user: null });
  };
  return (
    <CustomerCtx.Provider value={{ ...state, login, loginPhone, signup, logout, refresh: load }}>
      {children}
    </CustomerCtx.Provider>
  );
}
export const useCustomerAuth = () => useContext(CustomerCtx);
